import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type Theme } from '@mui/material/styles';

import { IconCamera } from '@/components/icon/tabler/IconCamera';
import { IconMapPin } from '@/components/icon/tabler/IconMapPin';
import { IconShieldCheck } from '@/components/icon/tabler/IconShieldCheck';
import { IconShoppingBag } from '@/components/icon/tabler/IconShoppingBag';
import { useTranslate } from '@/state/i18n';
import { controlTokens, surfaceTokens } from '@/theme/themePrimitives';

/**
 * What the portal offers each kind of partner (events, products and services, sports media) and
 * the review that covers them all (design P01), in place of the professional web app's orbit.
 */
const AuthPartnerHighlights = () => {
  const t = useTranslate('domain.auth.components.AuthPartnerHighlights');

  const items = [
    { key: 'events', Icon: IconMapPin },
    { key: 'offers', Icon: IconShoppingBag },
    { key: 'media', Icon: IconCamera },
    { key: 'approved', Icon: IconShieldCheck },
  ] as const;

  return (
    <Stack component="ul" sx={sx.list}>
      {items.map(({ key, Icon }) => (
        <Box component="li" key={key} sx={sx.item}>
          <Box sx={sx.icon}>
            <Icon fontSize="small" />
          </Box>
          <Stack gap={0.5}>
            <Typography component="span" variant="subtitle1" sx={sx.title}>
              {t(`${key}.title`)}
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
              {t(`${key}.description`)}
            </Typography>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default AuthPartnerHighlights;

const sx = {
  list: {
    listStyle: 'none',
    m: 0,
    p: 0,
    gap: { xs: 2, md: 3 },
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
  },
  icon: (theme: Theme) => ({
    width: 40,
    height: 40,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    bgcolor: surfaceTokens.paper,
    border: `1px solid ${surfaceTokens.borderPaper}`,
    color: controlTokens.primary,
    ...theme.applyStyles('dark', { bgcolor: 'background.paper', color: 'text.primary' }),
  }),
  title: (theme: Theme) => ({
    color: controlTokens.primary,
    fontWeight: 600,
    ...theme.applyStyles('dark', { color: 'text.primary' }),
  }),
};
